/**
 * Infrastructure Provider API
 *
 * Frontend API layer for managing virtualization infrastructure (Xen, AWS).
 * All calls go through the authenticated api client.
 */

import { api } from './client';

const BASE = '/integrations/infrastructure';

// ─── Types ──────────────────────────────────────────────────────

export interface InfraHost {
  id: string;
  name: string;
  displayName: string;
  provider: 'xen' | 'aws';
  port: number;
  mode: 'safe' | 'supervised' | 'autonomous';
  criticality: 'development' | 'standard' | 'critical';
  description?: string;
  purpose?: string[];
  discoveredResources?: DiscoveredResources;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DiscoveredResources {
  cpuTotal?: number;
  cpuModel?: string;
  memoryTotalMB?: number;
  storageRepositories?: Array<{
    id: string;
    name: string;
    type: string;
    totalSizeGB: number;
    freeSizeGB: number;
  }>;
  networks?: Array<{
    id: string;
    name: string;
    bridge?: string;
  }>;
  templateCount?: number;
  vmCount?: number;
  lastDiscoveredAt?: string;
}

export interface VMInfo {
  id: string;
  name: string;
  description?: string;
  state: 'running' | 'stopped' | 'paused' | 'suspended' | 'creating' | 'destroying' | 'error' | 'unknown';
  cpuCount: number;
  memoryMB: number;
  diskSizeGB?: number;
  ipAddresses: string[];
  templateName?: string;
  networkIds: string[];
  createdAt?: string;
  uptime?: number;
}

export interface HostResources {
  cpuTotal: number;
  cpuUsed: number;
  cpuModel?: string;
  memoryTotalMB: number;
  memoryUsedMB: number;
  memoryFreeMB: number;
  storageRepositories: StorageRepo[];
  networks: NetworkInfo[];
  vmCount: number;
}

export interface StorageRepo {
  id: string;
  name: string;
  type: string;
  totalSizeGB: number;
  usedSizeGB: number;
  freeSizeGB: number;
}

export interface NetworkInfo {
  id: string;
  name: string;
  bridge?: string;
  description?: string;
}

export interface TemplateInfo {
  id: string;
  name: string;
  description?: string;
  os?: string;
  cpuCount?: number;
  memoryMB?: number;
}

export interface VMCreateSpec {
  name: string;
  templateName: string;
  cpuCount: number;
  memoryMB: number;
  diskSizeGB?: number;
  networkIds?: string[];
  storageRepositoryId?: string;
  cloudInit?: {
    hostname?: string;
    sshAuthorizedKeys?: string[];
    packages?: string[];
    runcmd?: string[];
  };
  autoStart?: boolean;
  createServerConnection?: boolean;
  description?: string;
}

export interface InfraAuditLog {
  id: string;
  hostName: string;
  provider: string;
  operation: string;
  vmId?: string;
  vmName?: string;
  success: boolean;
  sanitizedOutput: string;
  executionTimeMs: number;
  timestamp: string;
}

// ─── Host Management ──────────────────────────────────────────

export async function registerHost(data: {
  name: string;
  displayName: string;
  provider: 'xen' | 'aws';
  host: string;
  username: string;
  password?: string;
  port?: number;
  mode?: string;
  description?: string;
  purpose?: string[];
}) {
  return api.post<InfraHost>(`${BASE}/hosts`, data);
}

export async function listHosts() {
  return api.get<InfraHost[]>(`${BASE}/hosts`);
}

export async function getHost(hostId: string) {
  return api.get<InfraHost>(`${BASE}/hosts/${hostId}`);
}

export async function updateHost(hostId: string, updates: Partial<InfraHost>) {
  return api.put<InfraHost>(`${BASE}/hosts/${hostId}`, updates);
}

export async function deleteHost(hostId: string) {
  return api.delete(`${BASE}/hosts/${hostId}`);
}

export async function transferHost(hostId: string, targetOrgSlug: string) {
  return api.post<InfraHost>(`${BASE}/hosts/${hostId}/transfer`, { targetOrgSlug });
}

// ─── Resource Discovery ───────────────────────────────────────

export async function discoverResources(hostId: string) {
  return api.post<HostResources>(`${BASE}/hosts/${hostId}/discover`);
}

// ─── VM Operations ────────────────────────────────────────────

export async function listVMs(hostId: string) {
  return api.get<VMInfo[]>(`${BASE}/hosts/${hostId}/vms`);
}

export async function getVM(hostId: string, vmId: string) {
  return api.get<VMInfo>(`${BASE}/hosts/${hostId}/vms/${vmId}`);
}

export async function createVM(hostId: string, spec: VMCreateSpec) {
  return api.post<VMInfo>(`${BASE}/hosts/${hostId}/vms`, spec);
}

export async function startVM(hostId: string, vmId: string) {
  return api.post(`${BASE}/hosts/${hostId}/vms/${vmId}/start`);
}

export async function stopVM(hostId: string, vmId: string, force = false) {
  return api.post(`${BASE}/hosts/${hostId}/vms/${vmId}/stop`, { force });
}

export async function destroyVM(hostId: string, vmId: string) {
  return api.post(`${BASE}/hosts/${hostId}/vms/${vmId}/destroy`);
}

export async function snapshotVM(hostId: string, vmId: string, name: string) {
  return api.post(`${BASE}/hosts/${hostId}/vms/${vmId}/snapshot`, { name });
}

// ─── Host Resource Queries ────────────────────────────────────

export async function listTemplates(hostId: string) {
  return api.get<TemplateInfo[]>(`${BASE}/hosts/${hostId}/templates`);
}

export async function listNetworks(hostId: string) {
  return api.get<NetworkInfo[]>(`${BASE}/hosts/${hostId}/networks`);
}

export async function listStorage(hostId: string) {
  return api.get<StorageRepo[]>(`${BASE}/hosts/${hostId}/storage`);
}

// ─── Audit Logs ───────────────────────────────────────────────

export async function getAuditLogs(params?: { hostId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.hostId) query.append('hostId', params.hostId);
  if (params?.limit) query.append('limit', params.limit.toString());
  if (params?.offset) query.append('offset', params.offset.toString());
  const qs = query.toString();
  return api.get<InfraAuditLog[]>(`${BASE}/audit${qs ? `?${qs}` : ''}`);
}

// ─── Formatters ───────────────────────────────────────────────

export function formatMemory(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb} MB`;
}

export function formatStorage(gb: number): string {
  if (gb >= 1024) return `${(gb / 1024).toFixed(1)} TB`;
  return `${gb.toFixed(1)} GB`;
}

export function vmStateColor(state: VMInfo['state']): string {
  switch (state) {
    case 'running': return 'text-green-600';
    case 'stopped': return 'text-gray-500';
    case 'paused': return 'text-yellow-600';
    case 'suspended': return 'text-blue-500';
    case 'creating': return 'text-purple-500';
    case 'error': return 'text-red-600';
    default: return 'text-gray-400';
  }
}

export function vmStateBadge(state: VMInfo['state']): string {
  switch (state) {
    case 'running': return 'bg-green-100 text-green-800';
    case 'stopped': return 'bg-gray-100 text-gray-800';
    case 'paused': return 'bg-yellow-100 text-yellow-800';
    case 'error': return 'bg-red-100 text-red-800';
    default: return 'bg-blue-100 text-blue-800';
  }
}
